var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getToken, login } from '../graphql/authentication';
import { loginDialogResponse } from '../lib/metrics';
import { log } from '../lib/logger';
import { captureException } from '../lib/sentry';
import { useLoginDialogStore, } from '../stores/loginDialogStore';
import { Deferred } from '../lib/Deferred';
import { showChatBoxParamName } from '../constants/locationParams';
import { useChatBoxStateStore } from '../stores/chatBoxStateStore';
import { useCreateConversation } from './useCreateConversation';
import { getPagesVisited } from './usePagesVisited';
import { Option } from 'effect';
const StartConversationOptionsDefaults = {
    showLoginDialog: true,
    flow: undefined,
    allowFirstMessage: false,
};
export const useGetProviderlessStartConversation = (lineOfBusiness, clientId, language, redirectUri) => {
    const { flow, setChatBoxFlow, setAllowFirstMessage } = useChatBoxStateStore(lineOfBusiness);
    const { createConversation } = useCreateConversation(lineOfBusiness, clientId, language);
    const [conversationStarted, setConversationStarted] = useState(false);
    const { setLoginDialogStatus } = useLoginDialogStore();
    const isLoggedIn = useMemo(() => __awaiter(void 0, void 0, void 0, function* () {
        const token = yield getToken(clientId);
        return Option.isSome(token);
    }), [clientId]);
    const showLoginDialog = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const loginConfirmationStatus = new Deferred();
        setLoginDialogStatus({ show: true, status: loginConfirmationStatus });
        const loginConfirmation = yield loginConfirmationStatus;
        loginDialogResponse(lineOfBusiness, loginConfirmation).catch(log.error);
        if (loginConfirmation === 'login') {
            login(clientId, redirectUri);
            return new Promise(() => { });
        }
        return undefined;
    }), [clientId, lineOfBusiness, redirectUri, setLoginDialogStatus]);
    useEffect(() => {
        var _a;
        const params = new URLSearchParams((_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.location.search);
        const showChatBoxState = params.get('state') === showChatBoxParamName;
        isLoggedIn
            .then(loggedIn => showChatBoxState && loggedIn && !conversationStarted)
            .then(autoCreateConversation => {
            if (autoCreateConversation) {
                setConversationStarted(true);
                return createConversation(flow, getPagesVisited(lineOfBusiness));
            }
            return null;
        })
            .catch(err => {
            log.error(err);
            captureException(err);
        });
    }, []);
    const startConversation = useCallback((options = {}) => __awaiter(void 0, void 0, void 0, function* () {
        const optionsValues = Object.assign(Object.assign({}, StartConversationOptionsDefaults), options);
        setChatBoxFlow(optionsValues.flow);
        setAllowFirstMessage(optionsValues.allowFirstMessage);
        if (!(yield isLoggedIn) && optionsValues.showLoginDialog) {
            yield showLoginDialog();
        }
        const sessionId = yield createConversation(optionsValues.flow, getPagesVisited(lineOfBusiness));
        return {
            created: true,
            sessionId,
        };
    }), [
        createConversation,
        isLoggedIn,
        showLoginDialog,
        setChatBoxFlow,
        setAllowFirstMessage,
        lineOfBusiness,
    ]);
    return { startConversation };
};
